/** Fixed English label when metadata.categories is present but empty (not translated). */
export const METADATA_CATEGORY_OTHERS_LABEL = 'Others';

export interface MetadataCategoryDisplay {
  label: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeCategoryValues(raw: unknown): string[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .filter((entry): entry is string => typeof entry === 'string')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

/**
 * True when the item explicitly defines a categories field on metadata or the root item.
 * metadata.categories takes precedence when the metadata object carries the key.
 */
export function hasMetadataCategoriesField(item: unknown): boolean {
  if (!isRecord(item)) {
    return false;
  }

  const metadata = item['metadata'];
  if (isRecord(metadata) && 'categories' in metadata) {
    return true;
  }

  return 'categories' in item;
}

/**
 * Returns trimmed category values when the field is present, otherwise null.
 */
export function extractMetadataCategories(item: unknown): string[] | null {
  if (!hasMetadataCategoriesField(item) || !isRecord(item)) {
    return null;
  }

  const metadata = item['metadata'];
  if (isRecord(metadata) && 'categories' in metadata) {
    return normalizeCategoryValues(metadata['categories']);
  }

  return normalizeCategoryValues(item['categories']);
}

/**
 * Resolves the single category label for metadata UI.
 * - Field absent → null (hide)
 * - Empty array → "Others" (fixed English, not translated)
 * - One value → that value
 * - Two or more → second value (index 1)
 */
export function resolveMetadataCategoryDisplay(item: unknown): MetadataCategoryDisplay | null {
  const values = extractMetadataCategories(item);
  if (values === null) {
    return null;
  }

  if (values.length === 0) {
    return { label: METADATA_CATEGORY_OTHERS_LABEL };
  }

  if (values.length === 1) {
    return { label: values[0] };
  }

  return { label: values[1] };
}
